import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCheck, Eye, EyeOff, Plus, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthActions } from '../context/authHooks';
import { assetUrl } from '../config';
import { uploadFile } from '../shared/api/uploadFile';
import { getMessageForCode } from '../shared/errors/getErrorMessage';
import ForgotPasswordForm from './ForgotPasswordForm';
import './ComponentLogin.css';

// 02.8-spaces-mail-shell.md paso b′ -- un `useForm()` por paso del wizard (§6), no un solo
// schema gigante para los 27 campos originales. `Login.jsx` vive en `src/pages/`, no
// `features/*/components/**`, así que no aplica la restricción de imports "presentacional"
// (§0.1) -- puede seguir usando `apiClient`/context directamente.
// `password` no tiene `.min(1)` a propósito -- el original solo marcaba `nick` como `required`
// (atributo nativo), la contraseña vacía sí llegaba a `login()` y el servidor la rechazaba.
const LoginStep0Schema = z.object({
    nick: z.string().min(1),
    password: z.string(),
});

const LoginStep1Schema = z.object({
    pwd1: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
    pwd2: z.string(),
}).refine((data) => data.pwd1 === data.pwd2, { message: 'Las contraseñas no coinciden', path: ['pwd2'] });

// `nickStatus`/`emailStatus` (disponibilidad, chequeada en el debounce) se quedan fuera del
// schema a propósito -- son estado externo asíncrono, no algo que Zod pueda validar de forma
// síncrona sobre el `values` de submit; `onStep2Submit` los revisa después de que este schema
// pasa, igual que el original `handleProfileNext`.
const LoginStep2Schema = z.object({
    newNick: z.string().min(1, 'El nick es obligatorio'),
    newEmail: z.string(),
});

// Sin validación real hoy (el original solo hacía `e.preventDefault(); setStep(4);`) -- el
// límite de 350 en `description` se aplica al tipear (autosize + slice), no al submit, así que
// no vive en el schema.
const LoginStep3Schema = z.object({
    description: z.string(),
    location: z.string(),
    religion: z.string(),
    birthDate: z.string(),
});

// Los 5 documentos KYC (idFront/idBack/proofDoc/curpDoc/birthCertDoc) se quedan como
// `useState` de archivo controlado fuera de RHF, misma decisión que el avatar del paso 2 (§6) --
// el presence-check vive en `onStep4Submit`, igual que el original. `rfc` es el único campo de
// texto real de este paso.
const LoginStep4Schema = z.object({
    rfc: z.string(),
});

const Login = () => {
    const { t } = useTranslation();
    const [step, setStep] = useState(0); // 0: Login, 1: Password Wizard, 2: Profile Wizard
    const [showForgotPassword, setShowForgotPassword] = useState(false);

    const step0Form = useForm({ resolver: zodResolver(LoginStep0Schema), defaultValues: { nick: '', password: '' } });
    const step1Form = useForm({ resolver: zodResolver(LoginStep1Schema), defaultValues: { pwd1: '', pwd2: '' } });
    const [pwd1, pwd2] = step1Form.watch(['pwd1', 'pwd2']);
    const step2Form = useForm({ resolver: zodResolver(LoginStep2Schema), defaultValues: { newNick: '', newEmail: '' } });
    const [newNick, newEmail] = step2Form.watch(['newNick', 'newEmail']);
    const step3Form = useForm({ resolver: zodResolver(LoginStep3Schema), defaultValues: { description: '', location: '', religion: '', birthDate: '' } });
    const description = step3Form.watch('description');
    const step4Form = useForm({ resolver: zodResolver(LoginStep4Schema), defaultValues: { rfc: '' } });

    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Wizard State
    const [profileImgFile, setProfileImgFile] = useState(null);
    const [previewImg, setPreviewImg] = useState('');
    const [tempUser, setTempUser] = useState(null);

    // New valid state
    const [showPwd, setShowPwd] = useState(false);
    const [nickStatus, setNickStatus] = useState('idle'); // 'idle' | 'valid' | 'invalid'
    const [emailStatus, setEmailStatus] = useState('idle');

    // Step 4 State
    const [idFront, setIdFront] = useState(null);
    const [idBack, setIdBack] = useState(null);
    const [proofDoc, setProofDoc] = useState(null);
    const [curpDoc, setCurpDoc] = useState(null);
    const [birthCertDoc, setBirthCertDoc] = useState(null);

    // File input refs for Step 4
    const idFrontRef = useRef(null);
    const idBackRef = useRef(null);
    const proofRef = useRef(null);
    const curpRef = useRef(null);
    const birthCertRef = useRef(null);

    // 02.8-spaces-mail-shell.md paso b′/§4 -- guard de request-id: sin esto, una respuesta
    // tardía de un debounce anterior podía pisar el resultado de uno más nuevo (race real,
    // encontrada en `/refine-plans`). Mismo tipo de fix que `requestIdRef` en LogsHistory (§0.8a).
    const nickRequestIdRef = useRef(0);
    const emailRequestIdRef = useRef(0);

    useEffect(() => {
        if (step !== 2) return;
        const delayDebounceFn = setTimeout(async () => {
            const requestId = ++nickRequestIdRef.current;
            if (newNick.length > 0) {
                const res = await fetch('/api/validate-nick', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ nick: newNick, userId: tempUser?.id || 0 })
                }).then(r => r.json());
                if (requestId !== nickRequestIdRef.current) return; // respuesta obsoleta
                setNickStatus(res.valid ? 'valid' : 'invalid');
            } else {
                setNickStatus('idle');
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [newNick, step, tempUser]);

    useEffect(() => {
        if (step !== 2) return;
        const delayDebounceFn = setTimeout(async () => {
            const requestId = ++emailRequestIdRef.current;
            if (newEmail.length > 0) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(newEmail)) {
                    setEmailStatus('invalid');
                    return;
                }
                const res = await fetch('/api/validate-email', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: newEmail, userId: tempUser?.id || 0 })
                }).then(r => r.json());
                if (requestId !== emailRequestIdRef.current) return; // respuesta obsoleta
                setEmailStatus(res.valid ? 'valid' : 'invalid');
            } else {
                setEmailStatus('idle');
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [newEmail, step, tempUser]);

    const fileInputRef = useRef(null);
    const navigate = useNavigate();
    // 03.1-auth-store.md §6 grupo 2 -- solo usa las acciones; `user` estaba destructurado pero
    // nunca se leía (código muerto -- las 2 refs a `.user` más abajo son de `result.user`, la
    // respuesta de `login()`, no este `user`).
    const { login, checkNickAvailability, uploadProfileImage } = useAuthActions();

    const onStep0Submit = async ({ nick, password }) => {
        setError('');
        setIsLoading(true);

        try {
            const result = await login(nick, password);
            if (result && result.success) {
                if (result.isNewUser) {
                    setTempUser(result.user);
                    step2Form.setValue('newNick', result.user.nick || '');
                    step2Form.setValue('newEmail', result.user.email || '');
                    setStep(1); // the user needs to create their password
                } else {
                    const params = new URLSearchParams(window.location.search);
                    const returnTo = params.get('returnTo') || '/';
                    navigate(returnTo);
                }
            } else {
                setError(getMessageForCode(result?.code));
            }
        } catch (err) {
            setError('Error al conectar con el servidor');
        } finally {
            setIsLoading(false);
        }
    };

    const onStep1Submit = () => {
        setStep(2);
    };

    const handleImageClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfileImgFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImg(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) {
            setProfileImgFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImg(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const onStep2Submit = () => {
        setError('');
        if (nickStatus === 'invalid') {
            setError('Nick ocupado');
            return;
        }
        if (newEmail.length > 0 && emailStatus === 'invalid') {
            setError('Correo inválido u ocupado');
            return;
        }
        setStep(3);
    };

    const onStep3Submit = () => {
        setStep(4);
    };

    const uploadFileHelper = async (file) => {
        if (!file) return null;
        try {
            return await uploadFile(file, 'document', { extraFields: { userId: tempUser.id } });
        } catch (e) {
            console.error('KYC upload error', e);
            return null;
        }
    };

    const onStep4Submit = async ({ rfc }) => {
        setError('');

        if (!idFront || !idBack || !proofDoc || !curpDoc || !birthCertDoc) {
            setError(t('auth.documentationRequired'));
            return;
        }

        setIsLoading(true);
        try {
            let imgUrl = tempUser['link-profile-img'];
            let thumbUrl = tempUser['link-profile-img-thumb'];
            if (profileImgFile) {
                try {
                    const url = await uploadFile(profileImgFile, 'img-avatar', { extraFields: { userId: tempUser.id } });
                    imgUrl = url;
                    thumbUrl = url;
                } catch (e) {
                    console.error('Profile image upload error', e);
                }
            }

            // Final Update User call and set welcome = 1. Identidad vía cookie de
            // sesión (ya emitida en el login inicial del wizard), no header manual.
            const updateRes = await fetch(`/api/users/${tempUser.id}`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nick: newNick,
                    email: newEmail,
                    password: pwd1,
                    welcome: 1,
                    'link-profile-img': imgUrl,
                    'link-profile-img-thumb': thumbUrl,
                    description: step3Form.getValues('description'),
                    location: step3Form.getValues('location'),
                    religion: step3Form.getValues('religion'),
                    birth_date: step3Form.getValues('birthDate'),
                    id_front_link: await uploadFileHelper(idFront),
                    id_back_link: await uploadFileHelper(idBack),
                    proof_address_link: await uploadFileHelper(proofDoc),
                    curp_link: await uploadFileHelper(curpDoc),
                    birth_cert_link: await uploadFileHelper(birthCertDoc),
                    rfc
                })
            }).then(r => r.json());

            if (updateRes.success) {
                // Log them in natively
                const finalLogin = await login(newNick, pwd1);
                if (finalLogin && finalLogin.success && !finalLogin.isNewUser) {
                    const params = new URLSearchParams(window.location.search);
                    const returnTo = params.get('returnTo') || '/';
                    navigate(returnTo);
                } else {
                    setError('Ocurrió un error al cargar la nueva sesión');
                }
            } else {
                setError(updateRes.message || 'Error al guardar el perfil');
            }

        } catch (err) {
            console.error(err);
            setError('Error al conectar con el servidor');
        } finally {
            setIsLoading(false);
        }
    };

    // Background GIF List
    const GIFS = [
        assetUrl('colonny_uploads/1775916783555_45c1fcf4aaae94a8ab0015e186070d22%20%281%29.gif'),
        assetUrl('colonny_uploads/1775916810834_668a21316e691594842eaa2d618862cc.gif'),
        assetUrl('colonny_uploads/1775916816094_7c84c5b7873501c95d78b6f38830bc7d.gif'),
        assetUrl('colonny_uploads/1775916828136_44645bb6b6a11c508e161569cb3fd092.gif'),
        assetUrl('colonny_uploads/1775916831933_7f64550c106a658dafd7380b62ed0c8e.gif'),
        assetUrl('colonny_uploads/1775916837022_c9efaed6586db6ec86eaed6ae45415d9.gif'),
        assetUrl('colonny_uploads/1775916840731_93266d8669bd48f3552c90c0de38949f.gif'),
        assetUrl('colonny_uploads/1775916847623_a3847a6ec3cbb2e6e13ba25dd5fde0a0.gif'),
        assetUrl('colonny_uploads/1775916861380_797555922a719c6a4082158d3f1a51f4.gif'),
        assetUrl('colonny_uploads/1775916867266_6ac780f0649e8e2497148d50edf432c3.gif'),
        assetUrl('colonny_uploads/1775916870717_773280be6000d60fddb18d673fba1ab8.gif'),
        assetUrl('colonny_uploads/1775916873642_089dbe8f9d422e09d2e4fcae0adec609.gif'),
        assetUrl('colonny_uploads/1775916876677_be6ded46b365626b0812a41b75875d59.gif'),
    ];

    const [bgGif, setBgGif] = React.useState('');

    React.useEffect(() => {
        const randomGif = GIFS[Math.floor(Math.random() * GIFS.length)];
        setBgGif(randomGif);
    }, []);

    return (
        <div style={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: '8vh', paddingBottom: '8vh', background: '#000000', position: 'relative', overflowX: 'hidden', overflowY: 'auto', boxSizing: 'border-box' }}>
            {/* Background Image Layer */}
            {bgGif && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundImage: `url("${bgGif}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(15px)', // applied blur
                    opacity: 0.6,
                    transform: 'scale(1.05)', // Prevent blur edges
                    zIndex: 0
                }} />
            )}
            
            {/* Dark Overlay for better contrast */}
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1
            }} />

            {/* Container wrapper for z-index */}
            <div style={{ position: 'relative', zIndex: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
                {step === 0 && showForgotPassword && (
                    <ForgotPasswordForm onBack={() => setShowForgotPassword(false)} />
                )}
                {step === 0 && !showForgotPassword && (
                <div style={{ position: 'relative', width: '100%', maxWidth: '377px' }}>
                    <form onSubmit={step0Form.handleSubmit(onStep0Submit)} className="component-login-container" style={{ margin: 0, maxWidth: '100%' }}>
                        <h2 className="login-title">{t('auth.welcome')}</h2>
                        <p className="login-subtitle">{t('auth.loginSubtitle')}</p>

                        {error && (
                            <div style={{ color: '#ef4444', fontSize: '0.875rem', textAlign: 'center', marginBottom: '1rem' }}>
                                {error}
                            </div>
                        )}

                        <div className="input-group">
                            <label className="input-label">{t('auth.usernameLabel')}</label>
                            <input
                                type="text"
                                className="input-field"
                                placeholder={t('auth.usernamePlaceholder')}
                                {...step0Form.register('nick')}
                            />
                        </div>

                        <div className="input-group" style={{ position: 'relative' }}>
                            <label className="input-label">{t('auth.passwordLabel')}</label>
                            <input
                                type={showPwd ? "text" : "password"}
                                className="input-field"
                                placeholder="••••••••"
                                style={{ paddingRight: '40px' }}
                                {...step0Form.register('password')}
                            />
                            <div
                                style={{ position: 'absolute', right: '12px', bottom: '14px', cursor: 'pointer', color: '#888' }}
                                onClick={() => setShowPwd(!showPwd)}
                            >
                                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                            </div>
                        </div>

                        <button type="submit" className="certify-btn" disabled={isLoading} style={{ width: '100%', marginTop: '9px' }}>
                            {isLoading ? 'Conectando...' : t('auth.certifyButton')}
                        </button>

                        <button
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            style={{ display: 'none', background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '0.8rem', cursor: 'pointer', marginTop: '0.5rem' }}
                        >
                            {t('auth.forgotPasswordLink')}
                        </button>
                    </form>
                </div>
            )}

            {step === 1 && (
                <div style={{ position: 'relative', width: '100%', maxWidth: '377px' }}>
                    {/* Wizard Dots */ }
                    <div style={{ position: 'absolute', top: '50%', right: '-24px', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fff' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />
                    </div>

                    <form onSubmit={step1Form.handleSubmit(onStep1Submit)} className="component-login-container" style={{ margin: 0, maxWidth: '100%' }}>
                        <h2 className="login-title">{t('auth.onboardingPasswordTitle')}</h2>
                        <p className="login-subtitle">{t('auth.onboardingPasswordSubtitle')}</p>

                        {step1Form.formState.isSubmitted && (step1Form.formState.errors.pwd1 || step1Form.formState.errors.pwd2) && (
                            <div style={{ color: '#ef4444', fontSize: '0.875rem', textAlign: 'center', marginBottom: '1rem' }}>
                                {step1Form.formState.errors.pwd1?.message || step1Form.formState.errors.pwd2?.message}
                            </div>
                        )}

                        <div className="input-group" style={{ position: 'relative' }}>
                            <label className="input-label">{t('auth.newPasswordLabel')}</label>
                            <input
                                type={showPwd ? "text" : "password"}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById('pwd2-input').focus(); } }}
                                className="input-field"
                                placeholder={t('auth.newPasswordPlaceholder')}
                                style={{ paddingRight: '40px' }}
                                {...step1Form.register('pwd1')}
                            />
                            <div
                                style={{ position: 'absolute', right: '12px', bottom: '14px', cursor: 'pointer', color: '#888' }}
                                onClick={() => setShowPwd(!showPwd)}
                            >
                                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                            </div>
                        </div>

                        <div className="input-group" style={{ position: 'relative' }}>
                            <label className="input-label">{t('auth.confirmPasswordLabel')}</label>
                            <input
                                id="pwd2-input"
                                type={showPwd ? "text" : "password"}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); step1Form.handleSubmit(onStep1Submit)(); } }}
                                className="input-field"
                                placeholder={t('auth.confirmPasswordPlaceholder')}
                                style={{ paddingRight: '40px' }}
                                {...step1Form.register('pwd2')}
                            />
                            {pwd1 && pwd1 === pwd2 && pwd1.length >= 8 && (
                                <div style={{ position: 'absolute', right: '17px', bottom: '0px', transform: 'translateY(50%)', color: '#95e248', display: 'flex', alignItems: 'center' }}>
                                    <CheckCheck size={20} />
                                </div>
                            )}
                        </div>
                    </form>
                    <button onClick={step1Form.handleSubmit(onStep1Submit)} className="certify-btn" style={{ width: '100%', marginTop: '9px' }}>
                        {t('auth.nextButton')}
                    </button>
                </div>
            )}

            {step === 2 && (
                <div style={{ position: 'relative', width: '100%', maxWidth: '377px' }}>
                    {/* Wizard Dots */ }
                    <div style={{ position: 'absolute', top: '50%', right: '-24px', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fff' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />
                    </div>

                    <form onSubmit={step2Form.handleSubmit(onStep2Submit)} className="component-login-container" style={{ padding: 0, margin: 0, maxWidth: '100%', background: 'var(--bg-panel)', position: 'relative' }}>
                        {/* Image Upload Area covers the top 250px */}
                        <div 
                            onClick={handleImageClick}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleDrop}
                            style={{
                                width: '100%',
                                height: '250px',
                                position: 'relative',
                                cursor: 'pointer',
                                backgroundColor: '#111',
                                borderTopLeftRadius: '1.5rem',
                                borderTopRightRadius: '1.5rem',
                                overflow: 'hidden'
                            }}
                        >
                            {previewImg ? (
                                <img src={previewImg} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                <div style={{ color: '#888', position: 'absolute', top: '35%', left: '0', width: '100%', textAlign: 'center', fontSize: '12px' }}>
                                    <Plus size={24} style={{ marginBottom: '4px', margin: '0 auto', display: 'block' }} />
                                    {t('auth.addProfilePhoto')}<br/>{t('auth.addProfilePhotoHint')}
                                </div>
                            )}

                            {/* Gradient Overlay */}
                            <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                width: '100%',
                                height: '200px',
                                background: 'linear-gradient(to bottom, transparent 0%, var(--bg-panel) 100%)',
                                pointerEvents: 'none'
                            }} />

                            <input 
                                type="file" 
                                accept="image/*" 
                                ref={fileInputRef} 
                                style={{ display: 'none' }}
                                onChange={handleImageChange}
                            />
                        </div>

                        {/* Inputs placed BELOW the image, sitting on the bottom of the gradient */}
                        <div style={{
                            padding: '0 2.5rem 2.5rem 2.5rem',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1.5rem',
                            position: 'relative',
                            zIndex: 10,
                            marginTop: '-40px' 
                        }}>

                            {(step2Form.formState.errors.newNick || error) && (
                                <div style={{ color: '#ef4444', fontSize: '0.875rem', textAlign: 'center', background: 'rgba(0,0,0,0.5)', padding: '4px', borderRadius: '4px' }}>
                                    {step2Form.formState.errors.newNick?.message || error}
                                </div>
                            )}

                            <div className="input-group" style={{ position: 'relative' }}>
                                <label className="input-label" style={{ color: '#ccc' }}>{t('auth.nickLabel')}</label>
                                <input
                                    type="text"
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById('new-email-input').focus(); } }}
                                    className="input-field"
                                    placeholder={t('auth.nickPlaceholder')}
                                    style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)', paddingRight: '36px' }}
                                    {...step2Form.register('newNick')}
                                />
                                {nickStatus === 'valid' && newNick && (
                                    <div style={{ position: 'absolute', right: '17px', bottom: '0px', transform: 'translateY(50%)', color: '#95e248', display: 'flex', alignItems: 'center' }}><CheckCheck size={20} /></div>
                                )}
                                {nickStatus === 'invalid' && newNick && (
                                    <>
                                        <div style={{ position: 'absolute', right: '17px', bottom: '0px', transform: 'translateY(50%)', color: '#ef4444', display: 'flex', alignItems: 'center' }}><CheckCheck size={20} /></div>
                                        <div style={{ position: 'absolute', right: '40px', bottom: '0px', transform: 'translateY(50%)', background: '#ef4444', color: '#fff', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>{t('auth.nickTaken')}</div>
                                    </>
                                )}
                            </div>

                            <div className="input-group" style={{ position: 'relative' }}>
                                <label className="input-label" style={{ color: '#ccc' }}>{t('auth.emailLabel')}</label>
                                <input
                                    id="new-email-input"
                                    type="email"
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); step2Form.handleSubmit(onStep2Submit)(); } }}
                                    className="input-field"
                                    placeholder={t('auth.emailPlaceholder')}
                                    style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)', paddingRight: '36px' }}
                                    {...step2Form.register('newEmail')}
                                />
                                {emailStatus === 'valid' && newEmail && (
                                    <div style={{ position: 'absolute', right: '17px', bottom: '0px', transform: 'translateY(50%)', color: '#95e248', display: 'flex', alignItems: 'center' }}><CheckCheck size={20} /></div>
                                )}
                                {emailStatus === 'invalid' && newEmail && (
                                    <>
                                        <div style={{ position: 'absolute', right: '17px', bottom: '0px', transform: 'translateY(50%)', color: '#ef4444', display: 'flex', alignItems: 'center' }}><CheckCheck size={20} /></div>
                                        <div style={{ position: 'absolute', right: '40px', bottom: '0px', transform: 'translateY(50%)', background: '#ef4444', color: '#fff', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>{t('auth.emailTaken')}</div>
                                    </>
                                )}
                            </div>
                        </div>
                    </form>

                    <button
                        onClick={step2Form.handleSubmit(onStep2Submit)}
                        type="button"
                        className="certify-btn"
                        style={{ marginTop: '9px', width: '100%' }}
                    >
                        Siguiente
                    </button>
                </div>
            )}

            {step === 3 && (
                <div style={{ position: 'relative', width: '100%', maxWidth: '377px' }}>
                    {/* Wizard Dots */ }
                    <div style={{ position: 'absolute', top: '50%', right: '-24px', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fff' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />
                    </div>

                    <form onSubmit={step3Form.handleSubmit(onStep3Submit)} className="component-login-container" style={{ margin: 0, maxWidth: '100%' }}>
                        <h2 className="login-title">{t('auth.profileCompletionTitle')}</h2>
                        <p className="login-subtitle">{t('auth.profileCompletionSubtitle')}</p>

                        {error && (
                            <div style={{ color: '#ef4444', fontSize: '0.875rem', textAlign: 'center', marginBottom: '1rem' }}>
                                {error}
                            </div>
                        )}

                        <div className="input-group">
                            <label className="input-label">{t('auth.descriptionLabel')}</label>
                            <textarea
                                className="input-field"
                                placeholder={t('auth.descriptionPlaceholder')}
                                rows="3"
                                style={{ resize: 'none', overflow: 'hidden' }}
                                {...step3Form.register('description', {
                                    onChange: (e) => {
                                        const truncated = e.target.value.slice(0, 350);
                                        e.target.value = truncated;
                                        step3Form.setValue('description', truncated);
                                        e.target.style.height = 'auto';
                                        e.target.style.height = (e.target.scrollHeight) + 'px';
                                    },
                                })}
                            />
                            <div style={{ textAlign: 'right', fontSize: '10px', color: '#666', marginTop: '-4px' }}>{description.length}/350</div>
                        </div>

                        <div className="input-group">
                            <label className="input-label">{t('auth.locationLabel')}</label>
                            <input
                                type="text"
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById('religion-input').focus(); } }}
                                className="input-field"
                                placeholder={t('auth.locationPlaceholder')}
                                {...step3Form.register('location')}
                            />
                        </div>

                        <div className="input-group">
                            <label className="input-label">{t('auth.religionLabel')}</label>
                            <input
                                id="religion-input"
                                type="text"
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById('date-input').focus(); } }}
                                className="input-field"
                                placeholder={t('auth.religionPlaceholder')}
                                {...step3Form.register('religion')}
                            />
                        </div>

                        <div className="input-group">
                            <label className="input-label">{t('auth.birthDateLabel')}</label>
                            <input
                                id="date-input"
                                type="date"
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); step3Form.handleSubmit(onStep3Submit)(); } }}
                                className="input-field"
                                {...step3Form.register('birthDate')}
                            />
                        </div>
                    </form>

                    <button onClick={step3Form.handleSubmit(onStep3Submit)} disabled={isLoading} className="certify-btn" style={{ width: '100%', marginTop: '9px' }}>
                        {t('auth.nextButton')}
                    </button>
                </div>
            )}

            {step === 4 && (
                <div style={{ position: 'relative', width: '100%', maxWidth: '377px' }}>
                    {/* Wizard Dots */ }
                    <div style={{ position: 'absolute', top: '50%', right: '-24px', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fff' }} />
                    </div>

                    <form onSubmit={step4Form.handleSubmit(onStep4Submit)} className="component-login-container" style={{ margin: 0, maxWidth: '100%' }}>
                        <h2 className="login-title">{t('auth.documentationTitle')}</h2>
                        <p className="login-subtitle">{t('auth.documentationSubtitle')}</p>

                        {error && (
                            <div style={{ color: '#ef4444', fontSize: '0.875rem', textAlign: 'center', marginBottom: '1rem' }}>
                                {error}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px', marginBottom: '1.5rem' }}>
                            <div 
                                onClick={() => idFrontRef.current?.click()}
                                style={{ flex: 1, height: '100px', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: idFront ? '1px solid #95e248' : '1px dashed #555' }}>
                                {idFront ? <><CheckCheck size={24} color="#95e248"/><div style={{fontSize:'10px', marginTop:'4px', color:'#ccc'}}>{t('auth.idFront')} ✓</div></> : <><Upload size={24} color="#888"/><div style={{fontSize:'10px', marginTop:'4px', color:'#888'}}>{t('auth.idFront')}</div></>}
                                <input type="file" ref={idFrontRef} accept="image/*,application/pdf" style={{display:'none'}} onChange={(e) => setIdFront(e.target.files[0])} />
                            </div>
                            <div 
                                onClick={() => idBackRef.current?.click()}
                                style={{ flex: 1, height: '100px', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: idBack ? '1px solid #95e248' : '1px dashed #555' }}>
                                {idBack ? <><CheckCheck size={24} color="#95e248"/><div style={{fontSize:'10px', marginTop:'4px', color:'#ccc'}}>{t('auth.idBack')} ✓</div></> : <><Upload size={24} color="#888"/><div style={{fontSize:'10px', marginTop:'4px', color:'#888'}}>{t('auth.idBack')}</div></>}
                                <input type="file" ref={idBackRef} accept="image/*,application/pdf" style={{display:'none'}} onChange={(e) => setIdBack(e.target.files[0])} />
                            </div>
                        </div>

                        <div className="input-group">
                            <div 
                                onClick={() => proofRef.current?.click()}
                                style={{ width: '100%', height: '50px', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: proofDoc ? '1px solid #95e248' : '1px dashed #555' }}>
                                {proofDoc ? <div style={{fontSize:'14px', color:'#95e248'}}>{t('auth.proofOfAddress')} ✓</div> : <div style={{fontSize:'14px', color:'#888', display:'flex', alignItems:'center', gap:'8px'}}><Upload size={16}/> {t('auth.proofOfAddress')}</div>}
                                <input type="file" ref={proofRef} accept="image/*,application/pdf" style={{display:'none'}} onChange={(e) => setProofDoc(e.target.files[0])} />
                            </div>
                        </div>

                        <div className="input-group">
                            <div 
                                onClick={() => curpRef.current?.click()}
                                style={{ width: '100%', height: '50px', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: curpDoc ? '1px solid #95e248' : '1px dashed #555' }}>
                                {curpDoc ? <div style={{fontSize:'14px', color:'#95e248'}}>{t('auth.curp')} ✓</div> : <div style={{fontSize:'14px', color:'#888', display:'flex', alignItems:'center', gap:'8px'}}><Upload size={16}/> {t('auth.curp')}</div>}
                                <input type="file" ref={curpRef} accept="image/*,application/pdf" style={{display:'none'}} onChange={(e) => setCurpDoc(e.target.files[0])} />
                            </div>
                        </div>

                        <div className="input-group">
                            <div 
                                onClick={() => birthCertRef.current?.click()}
                                style={{ width: '100%', height: '50px', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: birthCertDoc ? '1px solid #95e248' : '1px dashed #555' }}>
                                {birthCertDoc ? <div style={{fontSize:'14px', color:'#95e248'}}>{t('auth.birthCertificate')} ✓</div> : <div style={{fontSize:'14px', color:'#888', display:'flex', alignItems:'center', gap:'8px'}}><Upload size={16}/> {t('auth.birthCertificate')}</div>}
                                <input type="file" ref={birthCertRef} accept="image/*,application/pdf" style={{display:'none'}} onChange={(e) => setBirthCertDoc(e.target.files[0])} />
                            </div>
                        </div>

                        <div className="input-group">
                            <label className="input-label">{t('auth.rfcLabel')}</label>
                            <input
                                type="text"
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); step4Form.handleSubmit(onStep4Submit)(); } }}
                                className="input-field"
                                placeholder="..."
                                {...step4Form.register('rfc')}
                            />
                        </div>
                    </form>

                    <button onClick={step4Form.handleSubmit(onStep4Submit)} disabled={isLoading} className="certify-btn" style={{ width: '100%', marginTop: '9px' }}>
                        {isLoading ? 'Finalizando...' : t('auth.startAdventureButton')}
                    </button>
                </div>
            )}
            </div>
        </div>
    );
};

export default Login;
